import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar } from 'lucide-react';
import blogPosts from '../../data/blogPosts.json';
import { getAllMarketUpdates } from '../../data/marketUpdateData';
import { useUserState } from '../../lib/userState';

// Top 3 cards mixing the latest market update with the most recent blog
// posts. Drop-in component for the homepage and any Hub page that wants
// editorial freshness without a dedicated news page.
//
// 2026-03-02: state-aware - only posts tagged `state === userState`
// or `state === 'AU'` (federal-scoped) are eligible so a VIC visitor
// never sees an SA-only news item on the homepage strip.
const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
};

const buildItems = (userState) => {
  const updates = getAllMarketUpdates() || [];
  // Market updates are SA-only today (the underlying market-update pages
  // are SA data). Suppress the market-update card for non-SA visitors.
  const latestUpdate = userState === 'SA' ? updates[0] : null;

  const updateCard = latestUpdate
    ? {
        kind: 'market-update',
        title: `${latestUpdate.month} ${latestUpdate.year} Market Update`,
        excerpt: latestUpdate.metaDescription || latestUpdate.summary,
        href: `/market-update/${latestUpdate.slug || ''}`.replace(/\/$/, '/market-update'),
        eyebrow: 'Adelaide build market',
        date: `${latestUpdate.month} ${latestUpdate.year}`,
      }
    : null;

  const eligiblePosts = (blogPosts || []).filter((p) => {
    const s = p.state || 'SA';
    return s === 'AU' || s === userState;
  });
  const recentPosts = eligiblePosts
    .slice()
    .sort((a, b) => {
      const da = new Date(a.publishDate || a.lastModified || 0).getTime();
      const db = new Date(b.publishDate || b.lastModified || 0).getTime();
      return db - da;
    })
    .slice(0, updateCard ? 2 : 3)
    .map((p) => ({
      kind: 'post',
      title: p.title,
      excerpt: p.excerpt,
      href: `/post/${p.slug}`,
      eyebrow: p.category || (p.categories && p.categories[0]) || 'Insights',
      date: fmtDate(p.publishDate || p.lastModified),
    }));

  return [updateCard, ...recentPosts].filter(Boolean).slice(0, 3);
};

const LatestNewsStrip = ({
  title = 'The latest from BuildPilot',
  subtitle = 'Independent reads on the Adelaide build market, costs and process. Updated regularly.',
  className = '',
}) => {
  const userState = useUserState();
  const items = buildItems(userState);
  if (!items.length) return null;

  // Highlight the first two words ("The latest") of the default title with
  // the dashboard-blue animated gradient. If a custom title is passed in,
  // render it untouched.
  const renderTitle = () => {
    if (title === 'The latest from BuildPilot') {
      return (
        <>
          <span className="gradient-text-blue">The latest</span> from BuildPilot
        </>
      );
    }
    return title;
  };

  return (
    <section className={`py-10 sm:py-20 bg-[#fafbfc] border-y border-gray-100 ${className}`} data-testid="latest-news-strip">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center sm:items-end sm:justify-between flex-wrap gap-4 mb-5 sm:mb-10">
          <div className="max-w-[640px] text-center sm:text-left">
            <h2 className="text-[22px] sm:text-[32px] font-bold text-[#1d1d1f] tracking-[-0.01em] leading-[1.15]">
              {renderTitle()}
            </h2>
            <p className="text-[13.5px] sm:text-[16px] text-gray-600 mt-1.5 leading-[1.45] line-clamp-2 sm:line-clamp-none">{subtitle}</p>
          </div>
          <Link
            to="/buildingtipsandtricks"
            className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#1d1d1f] hover:text-[#1d8efe] transition-colors"
            data-testid="latest-news-see-all"
          >
            See all articles <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-5">
          {items.map((it, i) => (
            <Link
              key={it.href + i}
              to={it.href}
              className="group bg-white border border-gray-200 rounded-2xl p-3.5 sm:p-6 hover:border-[#1d1d1f] hover:shadow-[0_10px_28px_rgba(0,0,0,0.06)] transition-all flex flex-col"
              data-testid={`latest-news-card-${i}`}
            >
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.04em] text-[#1d8efe]">
                  {it.eyebrow}
                </span>
                {it.date && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                    <Calendar className="w-3 h-3" />
                    {it.date}
                  </span>
                )}
              </div>
              <h3 className="text-[15px] sm:text-[17px] font-bold text-[#1d1d1f] leading-[1.3] mb-1.5 sm:mb-2 line-clamp-2 group-hover:text-[#1d8efe] transition-colors">
                {it.title}
              </h3>
              <p className="text-[13px] sm:text-[13.5px] text-gray-600 leading-[1.5] line-clamp-2 sm:line-clamp-3 mb-0 sm:mb-4 flex-1">
                {it.excerpt}
              </p>
              <span className="hidden sm:inline-flex items-center gap-1 text-[12.5px] font-medium text-[#1d1d1f] group-hover:text-[#1d8efe] transition-colors">
                Read more <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LatestNewsStrip;
